/**
 * @copyright Copyright 2024-2024 Epic Systems Corporation
 * @file Header for a TablePage
 * @module Epic.AppOrchard.Core.TablePage
 */

import { DownloadIcon, InfoIcon, WarningIcon } from "@chakra-ui/icons";
import { Box, BoxProps, Button, Heading } from "@chakra-ui/react";
import { AOTooltip } from "ao/components/Core";
import { memo, ReactNode } from "react";

interface IProps {
	text: string;
	recordName?: string;
	children?: ReactNode;
	tooltipContent?: ReactNode;
	tooltipLink?: string;
	tooltipIcon?: ReactNode;
	/**When this table page header is doubling as the page's topmost header, set this to true */
	isTopmostHeader?: boolean;
	isLoading?: boolean;
	recordTotal?: number;
	/**When set, renders an Export button that calls this function when clicked */
	exportOnClick?: () => void;
	/** When exportOnClick, recordTotal, and this are set, conditionally adds a title to the export button warning that not all results will be shown. */
	exportLimit?: number;
}

type Props = IProps & BoxProps;

/** Header for a TablePage */
export const TablePageHeader = memo((props: Props) => {
	const {
		text,
		recordName,
		children,
		tooltipContent,
		tooltipLink,
		tooltipIcon,
		isTopmostHeader,
		isLoading,
		recordTotal,
		exportOnClick,
		exportLimit,
		...rest
	} = props;

	return (
		<Box
			display={{ base: "flex", md: "inline-flex" }}
			pt={{ base: "0.5rem", md: 0 }}
			flexWrap="wrap"
			gridColumnGap="1.5rem"
			gridRowGap="0.5rem"
			{...rest}
		>
			<Heading as={isTopmostHeader ? "h1" : "h2"} alignSelf="center" flexGrow="1">
				{text}

				{tooltipContent && (
					<AOTooltip
						helptext={tooltipContent}
						link={tooltipLink}
						boxProps={{
							verticalAlign: "top",
							ml: "0.25rem",
						}}
					>
						{tooltipIcon || <InfoIcon color="mediumseagreen" />}
					</AOTooltip>
				)}
			</Heading>
			{exportOnClick && (
				<Button
					w="auto"
					onClick={exportOnClick}
					title={`Export the filtered list of ${recordName || "records"} to a .CSV file${
						recordTotal && exportLimit && recordTotal > exportLimit
							? `. Only ${exportLimit} rows can be exported at a time so some of the current results will not be in the export. Try updating the filters to narrow down the results further.`
							: ""
					}`}
					disabled={isLoading || recordTotal === 0}
					leftIcon={<DownloadIcon />}
					rightIcon={
						recordTotal && exportLimit && recordTotal > exportLimit ? (
							<WarningIcon color="red" />
						) : undefined
					}
				>
					Export Filtered {recordName || "Records"}
				</Button>
			)}
			{children}
		</Box>
	);
});
